import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Clock, Copy, ExternalLink, EyeOff, Globe, RefreshCcwDot } from "lucide-react";
import { isMobile } from "react-device-detect";
import { ui } from "@patient-watch/ui";
import { Badge } from "@patient-watch/ui/badge";
import { ResizablePanel, ResizablePanelGroup } from "@patient-watch/ui/resizable";
import { ScrollArea, ScrollBar } from "@patient-watch/ui/scroll-area";
import { useSelectAndProcessSchedule } from "@/features/schedules/api/processSchedule";
import { useScheduleQuery } from "@/features/schedules/api/selectSchedule";
import { useCopyScheduleMutation } from "@/features/schedules/api/copySchedule";
import { canAddAttribute, useMyAccessModes } from "@/features/clinical-access/api/selectMyAccessModes";
import ScheduleForm from "@/features/schedules/components/ScheduleForm";
import { SchedulePublicationDialog } from "@/features/schedules/components/SchedulePublicationDialog";
import TimingPreview from "@/features/schedules/components/TimingPreview";
import { cn } from "@/lib/utils";
import { useFormState } from "@/stores/formState";
import { Button } from "@patient-watch/ui/button";
import { useNav } from "../../../../utils/nav";
import { useAuth } from "@/utils/auth";
import { schedulePreviewLabel } from "@/features/navigation/services/entityDisplayLabel";
import { useState } from "react";

function ScheduleBuilderPage() {
    const { scheduleId } = Route.useParams();
    const { isDirty } = useFormState();
    const navigate = useNavigate();
    const auth = useAuth();
    const { data: accessModes } = useMyAccessModes();
    const canAddSchedules = auth.roles.isAdmin || canAddAttribute(accessModes, "schedules");
    const copyScheduleMutation = useCopyScheduleMutation();
    const [publicationOpen, setPublicationOpen] = useState(false);

    const { data: processedSchedule, isFetching, refetch, isStale } = useSelectAndProcessSchedule(scheduleId || "");
    const { data: schedule, isLoading, error } = useScheduleQuery(scheduleId || "");

    const handleDuplicate = async () => {
        if (!scheduleId || !canAddSchedules) return;

        const result = await copyScheduleMutation.mutateAsync({ scheduleId });
        if (result) {
            navigate({
                to: "/builder/schedules/$scheduleId",
                params: { scheduleId: result.schedule_id },
            });
        }
    };

    useNav([
        {
            label: "Builder",
            route: { to: "/builder" },
        },
        {
            label: "Schedules",
            route: {
                to: "/builder/schedules",
            },
        },
        {
            label: schedulePreviewLabel(schedule?.timings, undefined, "Schedule"),
            route: {
                to: "/builder/schedules/$scheduleId",
                params: { scheduleId: scheduleId || "" },
            },
        },
    ]);

    // Only show stale when form is dirty
    const shouldShowStale = isStale && isDirty;

    // Check if scheduleId is valid
    if (!scheduleId) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="space-y-4 text-center">
                    <AlertCircle className="mx-auto w-12 h-12 text-red-500" />
                    <div className="font-medium text-red-600 dark:text-red-400">Invalid schedule ID</div>
                    <div className="text-slate-500 dark:text-slate-400 text-sm">Please check the URL and try again</div>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="space-y-4 text-center">
                    <div className="mx-auto border-4 border-blue-500 border-t-transparent rounded-full w-12 h-12 animate-spin"></div>
                    <div className="font-medium text-slate-600 dark:text-slate-300">Loading schedule...</div>
                    <div className="text-slate-400 dark:text-slate-500 text-sm">
                        Please wait while we fetch your data
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="space-y-4 max-w-md text-center">
                    <AlertCircle className="mx-auto w-12 h-12 text-red-500" />
                    <div className="font-medium text-red-600 dark:text-red-400">Error loading schedule</div>
                    <div className="text-slate-500 dark:text-slate-400 text-sm">{error.message}</div>
                    <Button onClick={() => window.location.reload()} variant="outline" className="mt-4">
                        Try Again
                    </Button>
                </div>
            </div>
        );
    }

    if (!schedule) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="space-y-4 text-center">
                    <AlertCircle className="mx-auto w-12 h-12 text-orange-500" />
                    <div className="font-medium text-slate-600 dark:text-slate-300">Schedule not found</div>
                    <div className="text-slate-400 dark:text-slate-500 text-sm">
                        The schedule you're looking for doesn't exist
                    </div>
                </div>
            </div>
        );
    }

    const scheduleTitle = schedulePreviewLabel(schedule.timings, undefined, "Edit Schedule");

    return (
        <div className="min-h-screen">
            <div className="flex flex-wrap justify-between items-center gap-3 px-6 pt-4">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <h1 className="font-semibold text-slate-900 dark:text-slate-100 text-xl truncate">
                        {scheduleTitle}
                    </h1>
                    {schedule.is_published ? (
                        <Badge variant="secondary" className="gap-1">
                            <Globe className="w-3 h-3" />
                            Published
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-slate-500 dark:text-slate-300">
                            Draft
                        </Badge>
                    )}
                    {shouldShowStale && (
                        <Badge variant="secondary" className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Stale
                        </Badge>
                    )}
                    {isFetching && (
                        <Badge variant="outline" className="flex items-center gap-1">
                            <RefreshCcwDot className="w-3 h-3 animate-spin" />
                            Refreshing
                        </Badge>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" className="gap-2" asChild>
                        <Link
                            to="/schedules/$scheduleId"
                            target="_blank"
                            params={{
                                scheduleId: scheduleId || "",
                            }}
                        >
                            <ExternalLink className="w-4 h-4" />
                            View
                        </Link>
                    </Button>
                    {canAddSchedules ? (
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={handleDuplicate}
                            disabled={copyScheduleMutation.isPending}
                        >
                            <Copy className="w-4 h-4" />
                            {copyScheduleMutation.isPending ? "Duplicating..." : "Duplicate"}
                        </Button>
                    ) : null}
                    {auth.roles.isAdmin ? (
                        <Button variant="outline" size="sm" className="gap-2" onClick={() => setPublicationOpen(true)}>
                            {schedule.is_published ? <EyeOff className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                            {schedule.is_published ? "Unpublish" : "Publish"}
                        </Button>
                    ) : null}
                    <Button
                        onClick={() => refetch()}
                        disabled={isFetching}
                        variant="outline"
                        size="sm"
                        className="gap-2"
                    >
                        <RefreshCcwDot className={cn("w-4 h-4", isFetching && "animate-spin")} />
                        {isFetching ? "Refreshing..." : "Refresh"}
                    </Button>
                </div>
            </div>

            {auth.roles.isAdmin ? (
                <SchedulePublicationDialog
                    scheduleId={scheduleId}
                    isPublished={schedule.is_published ?? false}
                    open={publicationOpen}
                    onOpenChange={setPublicationOpen}
                />
            ) : null}

            <div className="p-6">
                <ResizablePanelGroup
                    direction={isMobile ? "vertical" : "horizontal"}
                    className={cn("gap-4 overflow-hidden", !isMobile && "h-[calc(100vh-140px)]")}
                >
                    <ResizablePanel defaultSize={65} className="min-h-0">
                        <ScrollArea className="h-full">
                            <div className="pr-2">
                                <ScheduleForm schedule={schedule} />
                            </div>
                            <ScrollBar orientation="vertical" />
                        </ScrollArea>
                    </ResizablePanel>
                    <ui.ResizableHandle
                        withHandle
                        className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
                    />
                    <ResizablePanel defaultSize={35} className="min-h-0">
                        <ScrollArea className="h-full">
                            <div>
                                <div className="flex justify-between items-center gap-2 mb-4 h-11">
                                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                                        <h2 className="font-semibold text-slate-900 dark:text-slate-100 text-lg leading-none">
                                            Schedule Preview
                                        </h2>
                                        {isFetching ? (
                                            <Badge variant="static" color="blue" className="gap-1">
                                                <RefreshCcwDot className="w-3 h-3 animate-spin" />
                                                Updating
                                            </Badge>
                                        ) : shouldShowStale ? (
                                            <Badge variant="static" color="orange" className="gap-1">
                                                <Clock className="w-3 h-3" />
                                                Outdated
                                            </Badge>
                                        ) : (
                                            <Badge variant="static" color="green" className="gap-1">
                                                <CheckCircle2 className="w-3 h-3" />
                                                Up to date
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                <TimingPreview showLocked={false} processedSchedule={processedSchedule || []} />
                            </div>
                            <ScrollBar orientation="vertical" />
                        </ScrollArea>
                    </ResizablePanel>
                </ResizablePanelGroup>
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_app/builder/schedules/$scheduleId")({
    component: ScheduleBuilderPage,
});
